import { Button } from '@cretesuite/design-system'
import {
  IonContent,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react'
import { IonIcon } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import {
  playCircleOutline,
  repeatOutline,
  stopCircleOutline,
  timeOutline,
} from 'ionicons/icons'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { GreetingHeader } from '../components/GreetingHeader'
import { TimeClockBanner } from '../components/timeclock/TimeClockBanner'
import { useTimeClock } from '../hooks/timeclock/useTimeClock'
import { useTimeClockTasks } from '../hooks/timeclock/useTimeClockTasks'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { useIonicRefresher } from '../hooks/useIonicRefresher'
import { ChangeTaskModal } from '../modals/timeclock/ChangeTaskModal'
import { ClockInModal } from '../modals/timeclock/ClockInModal'
import { ClockOutModal } from '../modals/timeclock/ClockOutModal'

export const TimeClockPage = () => {
  const { _ } = useLingui()
  const { user } = useCurrentUser()
  const { data: timeClock, loading, refetch, status } = useTimeClock()
  const { tasks } = useTimeClockTasks({ pause: loading })

  const [showClockIn, setShowClockIn] = useState(false)
  const [showClockOut, setShowClockOut] = useState(false)
  const [showChangeTask, setShowChangeTask] = useState(false)

  const { onRefresh: handleRefresh } = useIonicRefresher(refetch)

  const isClockedOut = status.slug === 'clockedOut'

  const handleClockInDismiss = () => {
    setShowClockIn(false)
    refetch()
  }

  const handleClockOutDismiss = () => {
    setShowClockOut(false)
    refetch()
  }

  const handleChangeTaskDismiss = () => {
    setShowChangeTask(false)
    refetch()
  }

  return (
    <IonPage>
      <GreetingHeader
        name={user?.firstName ?? undefined}
        subtitle={_(msg`What are you working on`)}
      />
      <IonContent>
        <IonRefresher slot='fixed' onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className='timeclock-page'>
          {loading && !timeClock ? (
            <div className='ion-text-center ion-padding'>
              <p>{_(msg`Loading...`)}</p>
            </div>
          ) : (
            <>
              <div className='timeclock-banner-wrap'>
                <TimeClockBanner
                  status={status}
                  taskName={timeClock?.task?.name}
                  startTime={timeClock?.startTime}
                  shiftStartTime={timeClock?.shift?.startTime}
                />
              </div>

              {/* Not gated on connectivity: clock in/out/change task all queue
                  offline (optimistic), and View History reads from cache. */}
              <div className='timeclock-actions'>
                {isClockedOut ? (
                  <Button
                    onClick={() => setShowClockIn(true)}
                    className='timeclock-btn timeclock-btn--clockin'
                  >
                    <IonIcon icon={playCircleOutline} slot='start' />
                    {_(msg`Clock In`)}
                  </Button>
                ) : (
                  <>
                    {tasks.length > 0 && (
                      <Button
                        onClick={() => setShowChangeTask(true)}
                        variant='secondary'
                        className='timeclock-btn timeclock-btn--change'
                      >
                        <IonIcon icon={repeatOutline} slot='start' />
                        {_(msg`Change Task`)}
                      </Button>
                    )}
                    <Button
                      onClick={() => setShowClockOut(true)}
                      variant='destructive'
                      className='timeclock-btn timeclock-btn--clockout'
                    >
                      <IonIcon icon={stopCircleOutline} slot='start' />
                      {_(msg`Clock Out`)}
                    </Button>
                  </>
                )}

                <Link
                  to='/timeclock/history'
                  className='timeclock-view-history'
                >
                  <IonIcon icon={timeOutline} />
                  {_(msg`View History`)}
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Inline modals — stay inside React tree to access URQL context */}
        <IonModal
          isOpen={showClockIn}
          onDidDismiss={handleClockInDismiss}
          className='timeclock-modal'
        >
          {showClockIn && (
            <ClockInModal onDismiss={() => setShowClockIn(false)} />
          )}
        </IonModal>

        <IonModal
          isOpen={showClockOut}
          onDidDismiss={handleClockOutDismiss}
          className='timeclock-modal'
        >
          {showClockOut && (
            <ClockOutModal onDismiss={() => setShowClockOut(false)} />
          )}
        </IonModal>

        <IonModal
          isOpen={showChangeTask}
          onDidDismiss={handleChangeTaskDismiss}
          className='timeclock-modal'
        >
          {showChangeTask && (
            <ChangeTaskModal onDismiss={() => setShowChangeTask(false)} />
          )}
        </IonModal>
      </IonContent>
    </IonPage>
  )
}
